import { Check, X } from "lucide-react";
import { DealScoreGauge } from "../DealScoreGauge";
import { PropertyImage } from "../PropertyImage";
import { MetricRow } from "./primitives";
import { fmtMoney, fmtSigned, fmtPct, dealProperty } from "./format";

/**
 * Hero analysis preview — renders the #1 live top deal. All numbers come from
 * /api/top-deals; metrics the API does not return are hidden, never invented.
 */
export function AnalysisPreview({ deal, loading, onOpen }) {
  if (loading) return <AnalysisPreviewSkeleton />;
  if (!deal) return <AnalysisPreviewEmpty />;

  const p = dealProperty(deal);
  const cf = deal.cash_flow_monthly;
  const coc = deal.cash_on_cash ?? deal.cash_on_cash_return;
  const address = p.address || "Address unavailable";
  const location = [p.city, p.state].filter(Boolean).join(", ") + (p.zip_code ? ` ${p.zip_code}` : "");

  return (
    <div className="rd-card overflow-hidden min-w-0 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]" data-testid="hero-analysis-preview">
      <div className="flex items-center justify-between px-5 py-3 border-b border-rd-border bg-rd-offwhite">
        <span className="rd-eyebrow text-rd-text-2 whitespace-nowrap">Live analysis · Top deal today</span>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-rd-green whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-rd-green" aria-hidden="true" /> Real listing data
        </span>
      </div>

      <div className="relative h-48 sm:h-56 bg-rd-navy">
        <PropertyImage property={p} alt={`${address}, ${location} — listing photo`} className="h-full" imgClassName="w-full h-48 sm:h-56 object-cover" allowZoom={false} />
      </div>

      <div className="p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5 min-w-0">
          <div className="min-w-0 flex-1">
            <p className="rd-h3 text-xl sm:text-2xl text-rd-text truncate" title={address}>{address}</p>
            <p className="text-sm text-rd-text-2">{location}</p>
            <p className="mt-3 font-mono font-bold text-3xl text-rd-text tabular-nums" data-testid="hero-preview-price">{fmtMoney(p.price)}</p>

            <div className="mt-4">
              <MetricRow label="Estimated Rent" value={fmtMoney(deal.estimated_monthly_rent)} />
              <MetricRow label="Mortgage (Est.)" value={fmtMoney(deal.monthly_mortgage)} />
              <MetricRow label="Cash Flow" value={cf != null ? `${fmtSigned(cf)}/mo` : "—"} tone={cf == null ? "neutral" : cf >= 0 ? "positive" : "negative"} emphasis testId="hero-preview-cash-flow" />
              <MetricRow label="Cap Rate" value={fmtPct(deal.cap_rate)} />
              {coc != null && <MetricRow label="Cash-on-Cash ROI" value={fmtPct(coc)} />}
              {deal.one_percent_rule != null && (
                <MetricRow
                  label="1% Rule"
                  value={<span className="inline-flex items-center gap-1 font-sans text-sm">{deal.one_percent_rule ? <><Check className="w-4 h-4 text-rd-green" aria-hidden="true" /> Pass</> : <><X className="w-4 h-4 text-rd-negative" aria-hidden="true" /> Fail</>}</span>}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col items-center justify-center sm:pl-6 sm:border-l border-rd-border">
            <DealScoreGauge score={deal.deal_score} size="xl" />
          </div>
        </div>

        <button type="button" onClick={onOpen} className="rd-btn rd-btn-outline-green w-full mt-6" data-testid="hero-preview-open">
          Open Full Analysis
        </button>
      </div>
    </div>
  );
}

export function AnalysisPreviewSkeleton() {
  return (
    <div className="rd-card overflow-hidden" aria-busy="true" aria-label="Loading live analysis preview" data-testid="hero-analysis-preview-loading">
      <div className="h-11 border-b border-rd-border bg-rd-offwhite" />
      <div className="rd-skeleton h-48 sm:h-56 rounded-none" />
      <div className="p-6 grid sm:grid-cols-[1fr_200px] gap-6">
        <div className="space-y-3">
          <div className="rd-skeleton h-6 w-3/4" />
          <div className="rd-skeleton h-4 w-1/2" />
          <div className="rd-skeleton h-9 w-1/3" />
          <div className="rd-skeleton h-40 w-full" />
        </div>
        <div className="flex items-center justify-center"><div className="rd-skeleton w-[184px] h-[184px] rounded-full" /></div>
      </div>
      <div className="px-6 pb-6"><div className="rd-skeleton h-11 w-full" /></div>
    </div>
  );
}

function AnalysisPreviewEmpty() {
  return (
    <div className="rd-card p-8 text-center" data-testid="hero-analysis-preview-empty">
      <DealScoreGauge score={0} size="lg" showLabel={false} className="opacity-40" />
      <p className="mt-4 text-lg font-bold text-rd-text">Live example temporarily unavailable</p>
      <p className="mt-1 text-sm text-rd-text-2">Search any address to run your own analysis in seconds.</p>
    </div>
  );
}
